"use client";

import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { Calendar, TrendingDown, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";

import useUserStore from "@/features/auth/store/useUserStore";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CoachMarker } from "@/components/CoachMarker";
import { useFinancesStore } from "@/features/finances/store/useFinancesStore";
import { currentLanguageLocale } from "@/i18n/getDateFnsLocale.ts";
import { Button } from "@/components/ui/button.tsx";
import PATHS from "@/routes/paths.ts";
import { ActivityChart } from "@/features/finances/components/ActivityChart";

export default function FinancesOverviewPage() {
  const { t } = useTranslation(["finances", "common", "help"]);
  const { user } = useUserStore();
  const { data: financesData } = useFinancesStore();

  // Check if user has credits payment plan
  const hasCreditsPlan =
    user?.specialRoleChecks?.payment_plan === "credits" &&
    user?.specialRoleChecks?.payment_plan_status === "active";

  if (!financesData) {
    return null; // Loading is handled by FinancesWrapper
  }

  const { current_balance, latest_purchases, latest_usages, monthly_statistics, charts } =
    financesData;

  const usageTrendGrowth = monthly_statistics.usage_trend_percentage;
  const isGrowthPositive = usageTrendGrowth >= 0;

  return (
    <ScrollArea className="relative h-[calc(100dvh_-_4rem)]">
      <title>{`${t("finances:overview.title", "Finances Overview")} - Ximly`}</title>

      {/* Background decorative elements */}
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="duration-0.75 absolute -bottom-50 -left-50 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 opacity-30 transition-colors"
        initial={{ opacity: 0 }}
        layoutId="circle1"
        transition={{
          default: { duration: 0.75 },
          layout: { duration: 0.75 },
        }}
      />
      <motion.div
        layout
        animate={{ opacity: 0.2 }}
        className="duration-0.75 absolute top-25 -right-50 size-120 rounded-full bg-gradient-to-br from-green-500/20 to-cyan-500/20 opacity-30 transition-colors"
        initial={{ opacity: 0.2 }}
        layoutId="circle2"
        transition={{
          layout: { duration: 0.75 },
        }}
      />

      <div className="container mx-auto max-w-7xl px-2 py-6 pt-0! sm:px-6 md:px-8">
        <div className="space-y-6">
          {/* Header with Credits Card */}
          <motion.div
            animate={{ opacity: 1, x: 0 }}
            className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-between"
            initial={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Title */}
            <CoachMarker
              description={t("help:coachMarkers.finances.header.description" as any)}
              id="finances-header"
              placement="bottom"
              title={t("help:coachMarkers.finances.header.title" as any)}
            >
              <div className="">
                <h1 className="text-accent-foreground text-3xl font-bold">
                  {t("finances:overview.title", "Finances Overview")}
                </h1>
                <p className="text-muted-foreground text-base">
                  {t("finances:overview.subtitle", "Track your earnings and manage your finances")}
                </p>
              </div>
            </CoachMarker>

            {/* Credits Card */}
            {hasCreditsPlan && (
              <div className="md:w-fit">
                <CoachMarker
                  description={t("help:coachMarkers.finances.credits.description" as any)}
                  id="finances-credits"
                  placement="bottom"
                  title={t("help:coachMarkers.finances.credits.title" as any)}
                >
                  <Card className="bg-foreground/50 relative overflow-hidden p-4 backdrop-blur-sm">
                    <div className="pointer-events-none absolute inset-0 overflow-hidden">
                      <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-blue-500/15" />
                      <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-purple-500/15" />
                    </div>
                    <div className="relative flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="bg-muted flex items-center justify-center rounded-xl p-3">
                          <Icon
                            className="text-muted-foreground"
                            height="1.5rem"
                            icon="tabler:coins"
                            width="1.5rem"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="text-muted-foreground text-sm font-medium">
                            {t("finances:overview.availableCredits", "Available Credits")}
                          </p>
                          <p className="text-accent-foreground text-2xl font-bold">
                            {current_balance.available_credits}
                          </p>
                        </div>
                      </div>
                      <Button asChild size="sm" variant={"foreground"}>
                        <Link to={PATHS.TUTOR.FINANCES.BUY_CREDITS}>
                          {t("finances:overview.credits.buyMore", "Buy More Credits")}
                          <Icon icon={"mdi:plus"} />
                        </Link>
                      </Button>
                    </div>
                  </Card>
                </CoachMarker>
              </div>
            )}
          </motion.div>

          {/* Monthly Statistics - Compact Row */}
          <motion.div
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="mb-4">
              <h3 className="text-accent-foreground text-xl font-semibold">
                {t("finances:overview.sections.monthlyStats", "This Month's Performance")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("finances:overview.monthlyStatsSubtitle", "Overview of your monthly activity")}
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Credits Used */}
              <Card className="bg-foreground/50 relative overflow-hidden backdrop-blur-sm">
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="absolute -top-8 -right-8 h-16 w-16 rounded-full bg-red-500/10" />
                </div>
                <div className="relative p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center rounded-lg bg-red-500/10 p-2">
                      <Icon
                        className="text-red-500"
                        height="1.25rem"
                        icon="tabler:minus"
                        width="1.25rem"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("finances:overview.metrics.monthlyCreditsUsed", "Credits Used")}
                      </p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-accent-foreground text-2xl font-bold">
                          {monthly_statistics.current_month.credits_used}
                        </p>
                        <div className="flex items-center gap-1">
                          {isGrowthPositive ? (
                            <TrendingUp className="h-3 w-3 text-red-500" />
                          ) : (
                            <TrendingDown className="h-3 w-3 text-green-500" />
                          )}
                          <span
                            className={`text-xs font-medium ${isGrowthPositive ? "text-red-500" : "text-green-500"}`}
                          >
                            {Math.abs(usageTrendGrowth).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Credits Purchased */}
              <Card className="bg-foreground/50 relative overflow-hidden backdrop-blur-sm">
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="absolute -top-8 -right-8 h-16 w-16 rounded-full bg-green-500/10" />
                </div>
                <div className="relative p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center rounded-lg bg-green-500/10 p-2">
                      <Icon
                        className="text-green-500"
                        height="1.25rem"
                        icon="tabler:plus"
                        width="1.25rem"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-muted-foreground text-sm font-medium">
                        {t("finances:overview.metrics.creditsPurchased", "Credits Purchased")}
                      </p>
                      <p className="text-accent-foreground text-2xl font-bold">
                        {monthly_statistics.current_month.credits_purchased}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </motion.div>

          {/* Recent Activity - Compact Design */}
          <motion.div
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="mb-4">
              <h3 className="text-accent-foreground text-xl font-semibold">
                {t("finances:overview.sections.recentTransactions", "Recent Activity")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("finances:overview.recentActivitySubtitle", "Latest purchases and usage")}
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Recent Purchases */}
              <Card className="bg-foreground/50 relative overflow-hidden backdrop-blur-sm">
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="absolute -top-8 -right-8 h-16 w-16 rounded-full bg-green-500/15" />
                  <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-emerald-500/15" />
                </div>

                <div className="relative p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex items-center justify-center rounded-lg bg-green-500/10 p-2">
                      <Icon
                        className="text-green-500"
                        height="1.25rem"
                        icon="tabler:coins"
                        width="1.25rem"
                      />
                    </div>
                    <div>
                      <h4 className="text-accent-foreground font-semibold">
                        {t("finances:overview.recentPurchases.title", "Purchases")}
                      </h4>
                      <p className="text-muted-foreground text-xs">
                        {t("finances:overview.recentPurchases.subtitle", "Latest credit purchases")}
                      </p>
                    </div>
                  </div>

                  <div className="h-32 overflow-hidden">
                    <ScrollArea className="h-full">
                      <div className="space-y-2">
                        {latest_purchases.length > 0 ? (
                          latest_purchases.slice(0, 3).map((purchase, index) => (
                            <motion.div
                              key={purchase.id}
                              animate={{ opacity: 1, x: 0 }}
                              className="bg-background/30 rounded-lg p-2 backdrop-blur-sm"
                              initial={{ opacity: 0, x: -20 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center justify-center rounded bg-green-500/10 p-1">
                                    <Icon
                                      className="text-green-500"
                                      height="0.75rem"
                                      icon="mdi:plus"
                                      width="0.75rem"
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-accent-foreground text-sm font-medium">
                                        {purchase.credits}{" "}
                                        {t("finances:buyCredits.credits", "credits")}
                                      </span>
                                    </div>
                                    <p className="text-muted-foreground truncate text-xs">
                                      {purchase.description}
                                    </p>
                                  </div>
                                </div>
                                <span className="text-muted-foreground flex-shrink-0 text-xs">
                                  {format(new Date(purchase.purchased_at), "MMM d", {
                                    locale: currentLanguageLocale(),
                                  })}
                                </span>
                              </div>
                            </motion.div>
                          ))
                        ) : (
                          <div className="flex h-24 items-center justify-center">
                            <div className="text-center">
                              <Icon
                                className="text-muted-foreground mx-auto mb-1"
                                height="1.5rem"
                                icon="tabler:coins-off"
                                width="1.5rem"
                              />
                              <p className="text-muted-foreground text-xs">
                                {t(
                                  "finances:overview.recentPurchases.noPurchases",
                                  "No recent purchases",
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </Card>

              {/* Recent Usage */}
              <Card className="bg-foreground/50 relative overflow-hidden backdrop-blur-sm">
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="absolute -top-8 -right-8 h-16 w-16 rounded-full bg-blue-500/15" />
                  <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-indigo-500/15" />
                </div>

                <div className="relative p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex items-center justify-center rounded-lg bg-blue-500/10 p-2">
                      <Calendar className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="text-accent-foreground font-semibold">
                        {t("finances:overview.recentUsage.title", "Usage")}
                      </h4>
                      <p className="text-muted-foreground text-xs">
                        {t("finances:overview.recentUsage.subtitle", "Latest credit usage")}
                      </p>
                    </div>
                  </div>

                  <div className="h-32 overflow-hidden">
                    <ScrollArea className="h-full">
                      <div className="space-y-2">
                        {latest_usages.length > 0 ? (
                          latest_usages.slice(0, 3).map((usage, index) => (
                            <motion.div
                              key={usage.id}
                              animate={{ opacity: 1, x: 0 }}
                              className="bg-background/30 rounded-lg p-2 backdrop-blur-sm"
                              initial={{ opacity: 0, x: -20 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center justify-center rounded bg-blue-500/10 p-1">
                                    <Icon
                                      className="text-blue-500"
                                      height="0.75rem"
                                      icon="tabler:minus"
                                      width="0.75rem"
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-accent-foreground text-sm font-medium">
                                        {t("finances:buyCredits.creditsCount", {
                                          count: Number.parseFloat(usage.credits_used),
                                        })}
                                      </span>
                                    </div>
                                    <p className="text-muted-foreground truncate text-xs">
                                      {usage.description}
                                    </p>
                                  </div>
                                </div>
                                <span className="text-muted-foreground flex-shrink-0 text-xs">
                                  {format(new Date(usage.used_at), "MMM d", {
                                    locale: currentLanguageLocale(),
                                  })}
                                </span>
                              </div>
                            </motion.div>
                          ))
                        ) : (
                          <div className="flex h-24 items-center justify-center">
                            <div className="text-center">
                              <Calendar className="text-muted-foreground mx-auto mb-1 h-6 w-6" />
                              <p className="text-muted-foreground text-xs">
                                {t("finances:overview.recentUsage.noUsage", "No recent usage")}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </Card>
            </div>
          </motion.div>

          {/* Activity Charts - GitHub-like visualization */}
          {charts && (
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="mb-4">
                <h3 className="text-accent-foreground text-xl font-semibold">
                  {t("finances:overview.recentActivityTitle", "Recent Activity")}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {t(
                    "finances:overview.recentActivityDescription",
                    "Visual overview of your recent financial activity",
                  )}
                </p>
              </div>

              <ActivityChart charts={charts} />
            </motion.div>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
